"use client"

import Link from "next/link"
import { useRouter } from "next/navigation"
import { useEffect, useRef, useState } from "react"
import { z } from "zod"
import { ConnectionSymbol } from "./connection-symbol.tsx"
import { PointAmount } from "./point-amount.tsx"
import { appLinkEnvironment } from "../lib/app-links"
import { isNativeApp } from "../lib/native-capabilities.ts"

const apiBase = process.env["NEXT_PUBLIC_API_BASE_URL"] ?? "/api"
const entryCredentialSchema = z.string().min(1).max(2_000)
const previewSchema = z.object({ mediaName: z.string().min(1).optional(), pointQuantity: z.string().regex(/^\d+(?:\.\d{1,6})?$/), payoutWon: z.number().int().positive() })
const continuationSchema = z.object({ kind: z.literal("continuation"), state: z.enum(["AUTHENTICATED", "CONSENTED", "LINKED"]), values: previewSchema.optional() })
const completedSchema = z.object({ creditWon: z.number().int().positive(), kind: z.literal("completed"), returnUrl: z.string().min(1).max(500).optional(), sourceWon: z.number().int().positive() })
const processingSchema = z.strictObject({ conversionId: z.uuid(), kind: z.literal("processing"), requestId: z.uuid() })
const androidStoreUrl = appLinkEnvironment(apiBase) === "dev" ? "https://appdistribution.firebase.google.com/testerapps/1:764435132862:android:f625c7852ab054338049ef" : "https://play.google.com/store/apps/details?id=com.ayininc.cashhub"
const iosStoreUrl = "https://apps.apple.com/app/id6800136349"

type EntryResponse = z.infer<typeof continuationSchema> | z.infer<typeof completedSchema> | z.infer<typeof processingSchema>
type EntryStep = "checking" | "start" | "consent" | "complete" | "processing" | "finished" | "failed"
type StorePlatform = "android" | "ios" | "desktop"

export const readEntryResponse = async (response: Response): Promise<EntryResponse | null> => {
  if (!response.ok) return null
  const body: unknown = await response.json().catch(() => null)
  const continuation = continuationSchema.safeParse(body)
  if (continuation.success) return continuation.data
  const completed = completedSchema.safeParse(body)
  if (completed.success) return completed.data
  const processing = processingSchema.safeParse(body)
  return processing.success ? processing.data : null
}

const entryRequest = async (path: "/entry" | "/entry/complete" | "/entry/consent-link" | "/entry/continue", credential: string): Promise<{ readonly result: EntryResponse | null; readonly status: number }> => {
  const options =
    path === "/entry"
      ? { credentials: "include" as const }
      : {
          body: JSON.stringify(path === "/entry/consent-link" ? { consent: true, consentVersion: "v1", credential } : { credential }),
          credentials: "include" as const,
          headers: { "content-type": "application/json" },
          method: "POST",
        }
  const response = await fetch(path === "/entry" ? `${apiBase}${path}?entry=${encodeURIComponent(credential)}` : `${apiBase}${path}`, options)
  if (!response.ok) return { result: null, status: response.status }
  return { result: await readEntryResponse(response), status: response.status }
}

const stepFromContinuation = (response: z.infer<typeof continuationSchema>): EntryStep => (response.state === "LINKED" ? "complete" : "consent")

export const entryConsentMessage = (values: z.infer<typeof previewSchema> | null): string => {
  if (values === null) return "제휴사 포인트를 캐시허브 포인트로 전환할게요."
  const [whole = "0", fraction] = values.pointQuantity.split(".")
  const quantity = `${BigInt(whole).toLocaleString("ko-KR")}${fraction === undefined ? "" : `.${fraction}`}`
  return `${values.mediaName ?? "제휴사"} ${quantity}포인트를 캐시허브 ${values.payoutWon.toLocaleString("ko-KR")}포인트로 전환할게요.`
}

export const EntryConversionSummary = ({ creditWon, returnUrl, sourceWon }: { readonly creditWon: number; readonly returnUrl?: string; readonly sourceWon: number }) => (
  <>
    <ConnectionSymbol state="success" />
    <header className="journey-heading">
      <p className="eyebrow">전환 완료</p>
      <h1>CashHub 잔액으로 적립했어요.</h1>
      <p className="phrase-safe-block">전환 결과를 확인해 주세요.</p>
    </header>
    <dl className="detail-grid" data-detail-grid>
      <div>
        <dt>전환 금액</dt>
        <dd>{sourceWon.toLocaleString("ko-KR")}원</dd>
      </div>
      <div>
        <dt>최종 적립</dt>
        <dd><PointAmount amount={creditWon} /></dd>
      </div>
    </dl>
    <div className="action-area">
      <a className="button primary" href="/home">
        CashHub에서 사용하기
      </a>
      {returnUrl === undefined ? null : (
        <a className="button ghost" href={returnUrl}>
          이전 앱으로 돌아가기
        </a>
      )}
    </div>
  </>
)

type EntryConversionProps = { readonly credential: string | null; readonly resumeAfterLogin?: boolean }

// A running WebView navigates between entry links without replacing the document.
// Keep consent, completion results and polling scoped to one transfer credential.
export const EntryConversionClient = (props: EntryConversionProps) => <EntryConversionSession key={props.credential} {...props} />

const EntryConversionSession = ({ credential, resumeAfterLogin = false }: EntryConversionProps) => {
  const router = useRouter()
  const [step, setStep] = useState<EntryStep>(credential === null ? "failed" : "checking")
  const [completed, setCompleted] = useState<z.infer<typeof completedSchema> | null>(null)
  const [preview, setPreview] = useState<z.infer<typeof previewSchema> | null>(null)
  const [consented, setConsented] = useState(false)
  const [pending, setPending] = useState(false)
  const [installPromptOpen, setInstallPromptOpen] = useState(false)
  const [storePlatform, setStorePlatform] = useState<StorePlatform>("desktop")
  const [nativeApp, setNativeApp] = useState(true)
  const installDialogRef = useRef<HTMLDialogElement | null>(null)
  useEffect(() => {
    if (isNativeApp()) return
    setNativeApp(false)
    const userAgent = window.navigator.userAgent
    setStorePlatform(/Android/i.test(userAgent) ? "android" : /iPhone|iPad|iPod/i.test(userAgent) ? "ios" : "desktop")
  }, [])

  useEffect(() => {
    const dialog = installDialogRef.current
    if (dialog === null) return
    if (installPromptOpen && !dialog.open) dialog.showModal()
    if (!installPromptOpen && dialog.open) dialog.close()
  }, [installPromptOpen])

  useEffect(() => {
    const parsed = entryCredentialSchema.safeParse(credential)
    if (!parsed.success) {
      setStep("failed")
      return
    }
    let active = true
    void entryRequest("/entry", parsed.data)
      .then(async ({ result }) => {
        if (!active) return
        if (result === null) {
          setStep("failed")
          return
        }
        if (result.kind === "completed") {
          setCompleted(result)
          setStep("finished")
          return
        }
        if (result.kind === "processing") {
          setStep("processing")
          return
        }
        setPreview(result.values ?? null)
        // The app already chose its conversion surface. Validate the existing
        // cookie before showing login; an entry preview alone is not a session.
        if (resumeAfterLogin || isNativeApp()) {
          const continuation = await entryRequest("/entry/continue", parsed.data)
          if (!active) return
          if (continuation.status === 401) {
            setStep("start")
            return
          }
          if (continuation.result?.kind !== "continuation") {
            setStep("failed")
            return
          }
          setPreview(continuation.result.values ?? result.values ?? null)
          setStep(stepFromContinuation(continuation.result))
          return
        }
        setStep(result.state === "LINKED" ? "complete" : "start")
        setInstallPromptOpen(result.state !== "LINKED" && !isNativeApp())
      })
      .catch(() => {
        if (active) setStep("failed")
      })
    return () => {
      active = false
    }
  }, [credential, resumeAfterLogin])

  useEffect(() => {
    if (step !== "processing") return
    const parsed = entryCredentialSchema.safeParse(credential)
    if (!parsed.success) return
    let active = true
    const poll = (): void => {
      void entryRequest("/entry/complete", parsed.data)
        .then(({ result }) => {
          if (!active || result === null) return
          if (result.kind === "completed") {
            setCompleted(result)
            setStep("finished")
          }
        })
        .catch(() => undefined)
    }
    const timer = setInterval(poll, 3_000)
    return () => {
      active = false
      clearInterval(timer)
    }
  }, [credential, step])

  const continueAfterLogin = async (): Promise<void> => {
    const parsed = entryCredentialSchema.safeParse(credential)
    if (!parsed.success || pending) return
    setPending(true)
    try {
      const response = await entryRequest("/entry/continue", parsed.data)
      if (response.status === 401) {
        router.push(`/login?next=${encodeURIComponent(`/entry?entry=${encodeURIComponent(parsed.data)}&resume=1`)}`)
        return
      }
      if (response.result?.kind !== "continuation") {
        setStep("failed")
        return
      }
      setPreview(response.result.values ?? null)
      setStep(stepFromContinuation(response.result))
    } catch {
      setStep("failed")
    } finally {
      setPending(false)
    }
  }

  const consentAndLink = async (): Promise<void> => {
    const parsed = entryCredentialSchema.safeParse(credential)
    if (!parsed.success || !consented || pending) return
    setPending(true)
    try {
      const response = await entryRequest("/entry/consent-link", parsed.data)
      setStep(response.result?.kind === "continuation" ? stepFromContinuation(response.result) : "failed")
    } catch {
      setStep("failed")
    } finally {
      setPending(false)
    }
  }

  const completeConversion = async (): Promise<void> => {
    const parsed = entryCredentialSchema.safeParse(credential)
    if (!parsed.success || pending) return
    setPending(true)
    try {
      const response = await entryRequest("/entry/complete", parsed.data)
      if (response.result?.kind === "processing") {
        setStep("processing")
        return
      }
      if (response.result?.kind !== "completed") {
        setStep("failed")
        return
      }
      setCompleted(response.result)
      setStep("finished")
    } catch {
      setStep("failed")
    } finally {
      setPending(false)
    }
  }

  const closeInstallPrompt = (): void => setInstallPromptOpen(false)

  const installPrompt = (
    <dialog
      aria-describedby="entry-install-description"
      aria-labelledby="entry-install-title"
      className="entry-install-dialog"
      data-entry-install-dialog
      onCancel={(event) => {
        event.preventDefault()
        closeInstallPrompt()
      }}
      ref={installDialogRef}
    >
      <button aria-label="앱 설치 안내 닫기" className="entry-install-close" onClick={closeInstallPrompt} type="button">
        ×
      </button>
      <p className="eyebrow">포인트 전환</p>
      <h2 id="entry-install-title">전환할 방법을 선택해 주세요</h2>
      <p id="entry-install-description">캐시허브 앱을 설치하거나 웹에서 로그인해 전환을 진행하세요.</p>
      <div className="entry-install-actions">
        {storePlatform !== "ios" ? (
          <a className="button primary" href={androidStoreUrl} rel="noreferrer" target="_blank">
            {storePlatform === "desktop" ? "Android 앱 설치하기" : "앱 설치하기"}
          </a>
        ) : null}
        {storePlatform !== "android" ? (
          <a className={storePlatform === "desktop" ? "button secondary" : "button primary"} href={iosStoreUrl} rel="noreferrer" target="_blank">
            {storePlatform === "desktop" ? "iPhone 앱 설치하기" : "앱 설치하기"}
          </a>
        ) : null}
        <button
          aria-busy={pending}
          className="button secondary"
          disabled={pending}
          onClick={() => {
            closeInstallPrompt()
            void continueAfterLogin()
          }}
          type="button"
        >
          웹에서 로그인하기
        </button>
      </div>
    </dialog>
  )

  if (step === "checking")
    return (
      <article aria-live="polite" className="journey-card entry-conversion-card" data-entry-state="checking">
        <ConnectionSymbol state="pending" />
        <p className="phrase-safe-block">전환 요청을 안전하게 확인하고 있어요.</p>
      </article>
    )

  if (step === "failed")
    return (
      <article aria-live="polite" className="journey-card entry-conversion-card" data-entry-state="failed">
        <ConnectionSymbol state="error" />
        <header className="journey-heading">
          <p className="eyebrow">전환 요청 확인</p>
          <h1>전환 요청을 확인하지 못했어요.</h1>
          <p className="phrase-safe-block">제휴사 앱에서 전환 요청을 다시 시작해 주세요.</p>
        </header>
        <Link className="button secondary" href="/home">
          캐시허브 홈으로
        </Link>
      </article>
    )

  if (step === "finished" && completed !== null)
    return (
      <article aria-live="polite" className="journey-card entry-conversion-card" data-entry-state="finished">
        <EntryConversionSummary creditWon={completed.creditWon} sourceWon={completed.sourceWon} {...(completed.returnUrl === undefined ? {} : { returnUrl: completed.returnUrl })} />
      </article>
    )

  if (step === "processing")
    return (
      <article aria-live="polite" className="journey-card entry-conversion-card" data-entry-state="processing">
        <ConnectionSymbol state="pending" />
        <header className="journey-heading">
          <p className="eyebrow">전환 확인 중</p>
          <h1>포인트 차감 결과를 안전하게 확인하고 있어요.</h1>
          <p className="phrase-safe-block">같은 거래번호로 상태만 확인하며 포인트를 다시 차감하지 않습니다. 확인이 끝나면 이 화면에 자동으로 반영됩니다.</p>
        </header>
      </article>
    )

  if (step === "complete")
    return (
      <article className="journey-card entry-conversion-card" data-entry-state="complete">
        <ConnectionSymbol />
        <header className="journey-heading">
          <p className="eyebrow">전환 확정</p>
          <h1>전환을 확정할까요?</h1>
          <p className="phrase-safe-block">확정하면 CashHub 잔액으로 적립됩니다.</p>
        </header>
        <div className="action-area">
          <button aria-busy={pending} className="button primary" disabled={pending} onClick={() => void completeConversion()} type="button">
            {pending ? "전환 확정 중" : "전환 확정하기"}
          </button>
        </div>
      </article>
    )

  if (step === "consent")
    return (
      <article className="journey-card entry-conversion-card" data-entry-state="consent">
        <ConnectionSymbol />
        <header className="journey-heading">
          <p className="eyebrow">전환 동의</p>
          <h1>{entryConsentMessage(preview)}</h1>
          <p className="phrase-safe-block">동의하면 로그인 계정과 제휴사 요청을 안전하게 연결합니다.</p>
        </header>
        <div className="action-area">
          <label className="check-row">
            <input checked={consented} onChange={(event) => setConsented(event.target.checked)} type="checkbox" />
            <span>전환 내용을 확인하고 동의합니다.</span>
          </label>
          <button aria-busy={pending} className="button primary" disabled={!consented || pending} onClick={() => void consentAndLink()} type="button">
            {pending ? "동의 확인 중" : "동의하고 계속하기"}
          </button>
        </div>
      </article>
    )

  return (
    <>
      <article className="journey-card entry-conversion-card" data-entry-state="start">
        <ConnectionSymbol />
        <header className="journey-heading">
          <p className="eyebrow">제휴사 포인트 전환</p>
          <h1>안전하게 전환을 시작할게요.</h1>
          <p className="phrase-safe-block">{nativeApp ? "로그인한 뒤 전환 동의를 확인해 주세요." : "앱을 설치하거나 웹에서 전환을 계속할 수 있어요."}</p>
        </header>
        <div className="action-area">
          {nativeApp || storePlatform === "desktop" ? null : (
            <a className="button primary" href={storePlatform === "ios" ? iosStoreUrl : androidStoreUrl} rel="noreferrer" target="_blank">
              앱 설치하기
            </a>
          )}
          <button aria-busy={pending} className={nativeApp || storePlatform === "desktop" ? "button primary" : "button secondary"} disabled={pending} onClick={() => void continueAfterLogin()} type="button">
            {pending ? "로그인 확인 중" : nativeApp ? "로그인하고 계속" : "웹에서 로그인하기"}
          </button>
        </div>
      </article>
      {installPrompt}
    </>
  )
}
